import * as Sentry from "@sentry/react";

import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import {
  formBooleanToBoolean,
  mapValidationErrorsDitto,
} from "Utils/formUtils";
import { Form, FormikProps } from "formik";

import { CheckPriceProps } from "Components/Features/CheckPrice";
import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { usePriceCalculationSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import React from "react";
import { useDispatch } from "react-redux";
import { HealthPriceRequestInfo } from "../healthInterfaces";
import { HEALTH_YOUR_HEALTH_STEP } from "../healthSteps";
import { HealthProductDetailsFormElements } from "./HealthProductDetailsFormElements";
import { HEALTH_PRODUCT_DETAILS_INITIAL_FORM_VALUES } from "./healthProductDetailsConstants";
import { HealthProductDetailsFormValues } from "./healthProductDetailsInterfaces";
import { validationSchema } from "./healthProductDetailsValidation";

export const HealthProductDetails = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const { priceCalculationResult } = usePriceCalculationSelector();
  const initialFormState = useFormState<HealthProductDetailsFormValues>(
    HEALTH_YOUR_HEALTH_STEP.id,
    HEALTH_PRODUCT_DETAILS_INITIAL_FORM_VALUES,
  );

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.HEALTH,
  );

  const handleSubmit = (form: HealthProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: HEALTH_YOUR_HEALTH_STEP.id,
        formState: form,
      }),
    );

    updatePriceRequest<
      Omit<HealthPriceRequestInfo, "dateOfBirth" | "isFemale">
    >({
      productPriceUrl: BackendProductInfo.HEALTH.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        isSmoker: formBooleanToBoolean(form.isSmoker),
      },
      includeGender: true,
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING}`,
        );
      }}
    >
      <ProductDetailsTile
        header={getText("healthform.pageheader.yourhealth")}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<HealthProductDetailsFormValues>) => {
          return (
            <Form>
              <HealthProductDetailsFormElements formProps={formProps} />
              {priceCalculationResult.error && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}
              <ScrollToFirstError />
              <ErrorsSummedUp
                errors={mapValidationErrorsDitto(
                  formProps.errors,
                  shortTitleComponents,
                )}
              />
              <NavigationButtons
                hidePreviousButton
                isLoading={priceCalculationResult.isLoading}
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING}
                stepId={HEALTH_YOUR_HEALTH_STEP.id}
              />
            </Form>
          );
        }}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};
